export const MAX_LEGEND_ITEMS_PER_COLUMN = 5;

/**
 * The share and the value take ~98px of a row before the label gets anything, which
 * is why a legend column has to be this wide before a second one is worth having.
 */
export const MIN_LEGEND_COLUMN_WIDTH_WITH_SHARE = 244;

export const chunkLegendItems = <T>(items: T[], size = MAX_LEGEND_ITEMS_PER_COLUMN): T[][] => {
  const columns: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    columns.push(items.slice(index, index + size));
  }
  return columns;
};

/**
 * How many columns the legend should use.
 *
 * `MAX_LEGEND_ITEMS_PER_COLUMN` sets the ceiling, but a column is only worth having if
 * there is width for it: in a KPI card, splitting 8 items into two columns leaves each
 * label ~65px and shreds it. `availableWidth` of 0 (before the first measure) falls back
 * to the item-count ceiling.
 */
export const getLegendColumnCount = (
  itemCount: number,
  availableWidth: number,
  minColumnWidth = MIN_LEGEND_COLUMN_WIDTH_WITH_SHARE,
  columnGap = 32
): number => {
  const maxColumns = Math.max(1, Math.ceil(itemCount / MAX_LEGEND_ITEMS_PER_COLUMN));
  if (!availableWidth) return maxColumns;

  const columnsThatFit = Math.floor((availableWidth + columnGap) / (minColumnWidth + columnGap));
  return Math.max(1, Math.min(maxColumns, columnsThatFit));
};

/** The share of a total, to the precision the dashboard design uses. */
export const formatShareOfTotal = (value: number, total: number): string =>
  total ? `${((value / total) * 100).toFixed(2)}%` : "0%";
